/**
 * Mention Markup Parser and Serializer
 * 
 * Handles conversion between display format and storage format:
 * - Display: "@Jay Coleman testing"
 * - Storage: "@[Jay Coleman](userId) testing"
 */

export type MentionUser = {
  id: string;
  display: string;
};

export type Segment =
  | { type: 'text'; text: string }
  | { type: 'mention'; id: string; display: string };

/**
 * Parse markup string into segments
 * Input: "@[Jay Coleman](id) hello @[Jane Doe](id2)"
 * Output: [mention, text, mention]
 */
export function parseMarkup(value: string): Segment[] {
  if (!value) return [];

  const segments: Segment[] = [];
  const mentionRegex = /@\[([^\]]+)\]\(([^)]+)\)/g;
  let lastIndex = 0;
  let match;

  while ((match = mentionRegex.exec(value)) !== null) {
    // Add any text before this mention
    if (match.index > lastIndex) {
      segments.push({
        type: 'text',
        text: value.substring(lastIndex, match.index),
      });
    }

    // Add the mention
    segments.push({
      type: 'mention',
      id: match[2],
      display: match[1],
    });

    lastIndex = match.index + match[0].length;
  }

  // Add any remaining text after the last mention
  if (lastIndex < value.length) {
    segments.push({
      type: 'text',
      text: value.substring(lastIndex),
    });
  }

  return segments;
}

/**
 * Serialize segments back to markup string
 * Input: [mention, text, mention]
 * Output: "@[Jay Coleman](id) hello @[Jane Doe](id2)"
 */
export function serializeSegments(segments: Segment[]): string {
  return segments
    .map((segment) => {
      if (segment.type === 'text') {
        return segment.text;
      } else {
        return `@[${segment.display}](${segment.id})`;
      }
    })
    .join('');
}

/**
 * Extract all unique mentions from segments
 */
export function extractMentions(segments: Segment[]): MentionUser[] {
  const uniqueIds = new Set<string>();
  const mentions: MentionUser[] = [];

  for (const segment of segments) {
    if (segment.type === 'mention' && !uniqueIds.has(segment.id)) {
      uniqueIds.add(segment.id);
      mentions.push({ id: segment.id, display: segment.display });
    }
  }

  return mentions;
}

/**
 * Convert segments to plain text (for display)
 * "@[Jay Coleman](id)" becomes "@Jay Coleman"
 */
export function segmentsToPlainText(segments: Segment[]): string {
  return segments
    .map((segment) => {
      if (segment.type === 'text') {
        return segment.text;
      } else {
        return `@${segment.display}`;
      }
    })
    .join('');
}

